import {useMemo} from 'react';
import {useAppSelector} from '@hooks';
import {MapView} from '@molecules';
import {
  type Area,
  type MeetingRoom,
  getAvailabilityStatus,
  getAvailabilityStatusFromData,
  getIsMapsUsabilityVariantsEnabled,
  getIsThreeDMapsEnabled,
  getMarkerState,
  getMeetingRoomSuggestionByBuildingId,
  groupByRoomStateAvailabilty,
} from '@lib/store';
import {useFacilityMarkers} from '../useFacilityMarkers';
import {RoomEventTarget} from '../../../../../../../submodules/map/mapiq-map/EventTarget';
import {AbsoluteViewportRestriction} from '../../../../../../../submodules/map/mapiq-map/AbsoluteViewportRestriction';
import {createSetStyleConfig, createSetStyleConfigOverrideEntry, createState, mergeState} from '@lib/utils';
import {RoomAvailabilityMarkerData, RoomAvailabilityStatus} from '@map/mapiq-map/markers/NodeMarkerData';
import {EventTarget} from '@map/mapiq-map/EventTarget';
import {VariantSelector} from './VariantSelector';
import {useUsabilityVariant} from '../HaNMaps/useUsabilityVariant';
import {useHereAndNow} from '@pages';

type HaNRoomsMapProps = {
  floorId: string;
  buildingId: string;
  onRoomClick: (e: RoomEventTarget) => void;
  onRoomHover?: (id: string | null) => void;
  onOutsideClick: () => void;
  hoverRoom: MeetingRoom | null;
  selectedRoom: MeetingRoom | Area | null;
  viewportRestrictions?: AbsoluteViewportRestriction;
};

export const HaNRoomsMap = ({
  buildingId,
  floorId,
  hoverRoom,
  onRoomHover,
  onRoomClick,
  viewportRestrictions,
  selectedRoom,
  onOutsideClick,
}: HaNRoomsMapProps) => {
  const {filterData, setHoverRoomId} = useHereAndNow();
  const {activeVariant} = useUsabilityVariant();

  const rooms = filterData.rooms.filteredData;
  const isMapsUsabilityVariantsEnabled = useAppSelector(getIsMapsUsabilityVariantsEnabled);
  const isThreeDMapsEnabled = useAppSelector(getIsThreeDMapsEnabled);
  const roomSuggestionDataEntities = useAppSelector((state) => getMeetingRoomSuggestionByBuildingId(state, buildingId));

  const roomOccupancyMarkers = useMemo(
    () => rooms.filter((room) => room.floorId === floorId).map((room) => ({type: 'room' as const, nodeId: room.id})),
    [rooms, floorId],
  );

  const roomAvailabilityMarkers = useMemo<RoomAvailabilityMarkerData[]>(
    () =>
      rooms
        .filter((room) => room.floorId === floorId && room.suggestionData)
        .map(
          (room): RoomAvailabilityMarkerData => ({
            type: 'room_availability',
            nodeId: room.id,
            status: getAvailabilityStatusFromData(roomSuggestionDataEntities, room),
          }),
        )
        .filter(({status}) => activeVariant === 'B' || status !== 'occupied'), // This variant hides occupied markers
    [rooms, floorId, roomSuggestionDataEntities, activeVariant],
  );

  const roomMarkers = useMemo(
    () => (isThreeDMapsEnabled ? roomAvailabilityMarkers : roomOccupancyMarkers),
    [isThreeDMapsEnabled, roomOccupancyMarkers, roomAvailabilityMarkers],
  );
  const facilityMarkers = useFacilityMarkers(buildingId, floorId);

  const highlights = useMemo(() => [...roomMarkers, ...facilityMarkers], [roomMarkers, facilityMarkers]);

  const groupedRooms = useMemo(() => {
    return rooms.reduce<Record<RoomAvailabilityStatus, MeetingRoom[]>>(
      (acc, room) => {
        if (room.id !== selectedRoom?.id) {
          const roomSuggestion = room.suggestionData
            ? roomSuggestionDataEntities?.data[room.suggestionData]
            : undefined;

          const state = isThreeDMapsEnabled
            ? getAvailabilityStatus(roomSuggestion)
            : groupByRoomStateAvailabilty(roomSuggestion);

          acc[state].push(room);
        }
        return acc;
      },
      {available: [], occupied: [], unknown: [], untraceable: []},
    );
  }, [rooms, roomSuggestionDataEntities, selectedRoom, isThreeDMapsEnabled]);

  const highlightedRooms = useMemo(
    () =>
      rooms.filter((room) => {
        // Treat all rooms as highlighted
        if (isThreeDMapsEnabled) return room.id !== selectedRoom?.id && room.suggestionData;

        return (
          room.id !== selectedRoom?.id &&
          room.suggestionData &&
          getMarkerState(roomSuggestionDataEntities?.data[room.suggestionData]) === 'highlighted'
        );
      }),
    [rooms, roomSuggestionDataEntities, selectedRoom, isThreeDMapsEnabled],
  );

  const dimmedRooms = useMemo(
    () =>
      rooms.filter(
        (room) =>
          room.suggestionData && getMarkerState(roomSuggestionDataEntities?.data[room.suggestionData]) === 'dimmed',
      ),
    [rooms, roomSuggestionDataEntities],
  );

  const allRooms = useMemo(() => {
    const availableRoomState = createState(
      highlightedRooms.filter((room) => room.id !== selectedRoom?.id).map((room) => room.id),
      'highlighted',
    );

    const dimmedRoomStates = createState(
      dimmedRooms.filter((room) => room.id !== selectedRoom?.id).map((room) => room.id),
      'dimmed',
    );

    const selectedRoomState = createState(selectedRoom ? [selectedRoom.id] : [], 'selected');

    return [dimmedRoomStates, availableRoomState, selectedRoomState].reduce(mergeState);
  }, [highlightedRooms, dimmedRooms, selectedRoom]);

  const elementsToKeepInView = useMemo(() => (selectedRoom ? [selectedRoom.id] : undefined), [selectedRoom]);

  const nodeStyles = useMemo(() => {
    if (!isThreeDMapsEnabled) return undefined;

    const available = activeVariant === 'B' ? 'availableB' : 'available';
    const occupied = activeVariant === 'B' ? 'occupiedB' : 'occupied';

    return createSetStyleConfig({
      preset: 'base',
      override: new Map([
        ...groupedRooms.available.map((room) => createSetStyleConfigOverrideEntry(room.id, available)),
        ...groupedRooms.occupied.map((room) => createSetStyleConfigOverrideEntry(room.id, occupied)),
        ...groupedRooms.unknown.map((room) => createSetStyleConfigOverrideEntry(room.id, 'seemsToBeAvailable')),
        ...groupedRooms.untraceable.map((room) => createSetStyleConfigOverrideEntry(room.id, 'base')),
        createSetStyleConfigOverrideEntry('room', 'available'),
      ]),
    });
  }, [activeVariant, groupedRooms, isThreeDMapsEnabled]);

  const handleHover = (e: EventTarget) => {
    if (!isThreeDMapsEnabled) return;

    const validId = e.areaId ?? e.roomId ?? e.facilityId;
    if (validId) {
      setHoverRoomId?.(validId);
      return;
    }

    setHoverRoomId?.(null);
  };

  const handleClick = (e: RoomEventTarget) => {
    if (e.type === 'room') {
      onRoomClick(e);
      return;
    }

    onOutsideClick();
  };

  return (
    <>
      <MapView
        {...(isMapsUsabilityVariantsEnabled && {key: activeVariant})}
        borderRadius={0}
        floorId={floorId}
        buildingId={buildingId}
        highlights={highlights}
        fullView={true}
        elementsToKeepInView={elementsToKeepInView}
        disablePointerEvents={false}
        buildingNodeStates={allRooms}
        buildingNodeTypeStates={new Map([['facility', 'dimmed']])}
        viewportRestrictions={viewportRestrictions}
        onClick={(e) => handleClick(e as RoomEventTarget)}
        spotlightId={hoverRoom?.floorId === floorId ? hoverRoom.id : null}
        type="preference"
        nodeStyles={nodeStyles}
        onHover={handleHover}
      />

      {isThreeDMapsEnabled && isMapsUsabilityVariantsEnabled && <VariantSelector />}
    </>
  );
};
